// Modules
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Defers a callback while someone is working inside a container.
 *
 * The problem this solves is a shared list the server can update at any moment:
 * applying an update while someone is partway through a row reorders the thing
 * under their cursor. Only the client knows whether the user is mid-task, so the
 * client picks the moment. The rule is a quiet period rather than a list of
 * special cases — any interaction inside the watched container restarts a timer,
 * and the call goes through once the timer runs out without further interaction.
 * If nothing is going on, the call happens immediately and none of this is
 * visible.
 *
 * Nothing in here knows what it is gating. It takes a function and a container to
 * watch, and hands back a version of that function that respects the quiet
 * period:
 *
 *   const { call, flush, pending, progress } = useGatedCallback(
 *     () => queryClient.refetchQueries(TRIAGE_KEY),
 *     { selector: '[data-triage-queue]', holdMs: 5000 }
 *   );
 *
 * The selector doubles as the key. Two callers watching the same container are by
 * definition talking about the same gate, so they share one timer, one pending
 * call and one progress reading — no matter how many components mount the hook.
 * That is also what lets a component which only wants to *display* the wait sit
 * anywhere in the tree with nothing threaded down to it:
 *
 *   const { flush, pending, progress } = useGatedCallback(null, {
 *     selector: '[data-triage-queue]',
 *     progress: true
 *   });
 */

const DEFAULT_HOLD_MS = 5000;

/**
 * Interactions that count. Deliberately not pointermove or scroll: moving the
 * pointer across a container or scrolling past a row is not work in progress, and
 * counting them would hold a call indefinitely.
 */
const DEFAULT_EVENTS = ['pointerdown', 'keydown', 'focusin'];

/** Drives progress. Only runs while a call is actually waiting. */
const TICK_MS = 100;

/** Stable, so a subscriber that never sees a hold never re-renders. */
const IDLE = { pending: false, progress: 0, holdMs: DEFAULT_HOLD_MS };

/** One gate per container selector — see the note on keying above. */
const gates = new Map();

function getGate(selector) {
  let gate = gates.get(selector);

  if (!gate) {
    gate = {
      selector,
      holdMs: DEFAULT_HOLD_MS,
      events: DEFAULT_EVENTS,
      lastInteractionAt: 0,
      // At most one, see requestCall.
      pending: null,
      ticker: null,
      trackers: 0,
      detach: null,
      listeners: new Set()
    };

    gates.set(selector, gate);
  }

  return gate;
}

function stateOf(gate) {
  if (!gate.pending) return { pending: false, progress: 0, holdMs: gate.holdMs };

  const elapsed = Date.now() - gate.lastInteractionAt;

  return {
    pending: true,
    progress: Math.min(1, Math.max(0, elapsed / gate.holdMs)),
    holdMs: gate.holdMs
  };
}

/**
 * `progressOnly` skips subscribers that asked for status but not progress, so a
 * held call re-renders them twice — once on hold, once on release — rather than
 * ten times a second. Callers that only need `call` are the common case, and they
 * are usually the expensive component.
 */
function notify(gate, progressOnly) {
  const state = stateOf(gate);

  gate.listeners.forEach(entry => {
    if (progressOnly && !entry.progress) return;

    entry.notify(state);
  });
}

function stopTicking(gate) {
  if (!gate.ticker) return;

  clearInterval(gate.ticker);
  gate.ticker = null;
}

function release(gate) {
  const run = gate.pending;

  gate.pending = null;
  stopTicking(gate);
  notify(gate, false);

  if (run) run();
}

function tick(gate) {
  if (!gate.pending) {
    stopTicking(gate);
    return;
  }

  if (Date.now() - gate.lastInteractionAt >= gate.holdMs) {
    release(gate);
    return;
  }

  notify(gate, true);
}

function requestCall(gate, run) {
  if (Date.now() - gate.lastInteractionAt >= gate.holdMs) {
    run();
    return;
  }

  // One pending call per gate: a later request supersedes an earlier one. This is
  // what collapses several components asking for the same refresh during one hold
  // into a single call, and it is why the invocation is captured here rather than
  // registered up front — whoever asked last is the one whose arguments apply.
  const wasPending = Boolean(gate.pending);

  gate.pending = run;

  if (!gate.ticker) gate.ticker = setInterval(() => tick(gate), TICK_MS);

  // Only entering the hold is a status change. Superseding a call while already
  // held changes nothing anyone is displaying, and notifying would re-render the
  // list on every message that arrives during the wait.
  if (!wasPending) notify(gate, false);
}

function noteInteraction(gate) {
  gate.lastInteractionAt = Date.now();

  // Progress restarts, but pending has not changed.
  if (gate.pending) notify(gate, true);
}

/**
 * Refcounted: several components mount the same gate, and the listeners belong to
 * the gate rather than to any one of them. Capture phase so a handler that stops
 * propagation cannot hide the interaction.
 */
function track(gate) {
  gate.trackers += 1;

  if (gate.trackers === 1) {
    const onInteraction = event => {
      const target = event.target;

      if (!target || typeof target.closest !== 'function') return;
      if (!target.closest(gate.selector)) return;

      noteInteraction(gate);
    };

    const events = gate.events;

    events.forEach(event => document.addEventListener(event, onInteraction, true));
    gate.detach = () => events.forEach(event => document.removeEventListener(event, onInteraction, true));
  }

  return () => {
    gate.trackers -= 1;
    if (gate.trackers > 0) return;

    if (gate.detach) gate.detach();
    gate.detach = null;
  };
}

function subscribe(gate, notifyFn, progress) {
  const entry = { notify: notifyFn, progress };

  gate.listeners.add(entry);
  notifyFn(stateOf(gate));

  return () => gate.listeners.delete(entry);
}

/**
 * @param fn        The function to gate. Null is valid — pass null to read status
 *                  without owning the call.
 * @param selector  The container to watch, and the gate's key.
 * @param holdMs    Quiet period after the last interaction. Shared per gate; only
 *                  an explicitly passed value sets it, so a status-only caller
 *                  does not reset it to the default.
 * @param events    Interaction events that count. Read when the first caller for
 *                  a gate mounts.
 * @param progress  Re-render this caller as progress advances. Off by default —
 *                  see notify().
 */
export default function useGatedCallback(fn, options = {}) {
  const { selector, holdMs, events, progress = false } = options;

  // Held in a ref so `call` can stay stable across renders. Callers pass an inline
  // arrow, and an unstable `call` would re-register the socket listener that
  // depends on it on every single render.
  const fnRef = useRef(fn);
  fnRef.current = fn;

  const [state, setState] = useState(IDLE);

  // Arrays are new on every render; the contents are what matter.
  const eventKey = events ? events.join(',') : '';

  useEffect(() => {
    if (!selector) return undefined;

    const gate = getGate(selector);

    if (holdMs !== undefined) gate.holdMs = holdMs;
    if (events !== undefined) gate.events = events;

    const unsubscribe = subscribe(gate, setState, progress);
    const untrack = track(gate);

    return () => {
      unsubscribe();
      untrack();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selector, holdMs, eventKey, progress]);

  const call = useCallback((...args) => {
    if (!selector) return;

    requestCall(getGate(selector), () => {
      if (fnRef.current) fnRef.current(...args);
    });
  }, [selector]);

  /** Skip the rest of the quiet period and run the pending call now. */
  const flush = useCallback(() => {
    if (!selector) return;

    const gate = getGate(selector);
    if (gate.pending) release(gate);
  }, [selector]);

  return { call, flush, ...state };
}

/** Test hook. */
export function resetGates() {
  gates.forEach(gate => {
    stopTicking(gate);
    if (gate.detach) gate.detach();
  });

  gates.clear();
}
